import { Outlet, useLoaderData } from "react-router-dom";
import { useAtom } from "jotai";
import { uiContextState } from "@/custolink-state/ui_context_state";
import EnvironmentBanner from "@/custolink-components/layout/EnvironmentBanner";
import { useEffect, useState } from "react";
import { loadAdminAreaServiceWorker } from "@/load_service_worker";
import { uiAdminAreaAuthState } from "@/custolink-state/ui_admin_area_auth_state";
import { uiAssetFrontendAuthState } from "@/custolink-state/ui_asset_frontend_auth_state";
import {
  AdmUserApi,
  AdmUserApi$AuthInfoPermission,
  AdmUserApi$CurrentAuthedUser,
  WebAppMainApi,
  WebAppMainApi$UiContext,
  AssetFrontendUserApi,
  AssetFrontendUserApi$CurrentAuthedUser,
} from "@/custolink-transpiled_by_server/bc.identity_and_access.api_methods";

export const contextAndAuthLoader = async (adminArea: boolean) => {
  const c = await WebAppMainApi.getUiContext(document.location.hostname);

  if (adminArea && c.organisationContext != null) {
    await loadAdminAreaServiceWorker();
  }

  return [
    adminArea,
    c,
    adminArea ? await getAdminAreaAuthUser() : null,
    !adminArea ? await getAssetFrontendUser() : null,
  ];

 // return [adminArea, c, null, ""];
};

async function getAdminAreaAuthUser() {
  try {
    return await AdmUserApi.getCurrentAuthedUser();
  } catch (err: unknown) {
    const error = err as { status: number };
    if (error.status == 401 || error.status == 403) {
      return null
    }
    throw err;
  }
}

async function getAssetFrontendUser() {
  try {
    return await AssetFrontendUserApi.getCurrentAuthedUser();
  } catch (err: unknown) {
    const error = err as { status: number };
    if (error.status == 401 || error.status == 403) {
      return null;
    }
    throw err;
  }
}

export const Context = () => {
  const [adminArea, uicontext, adminAreaAuthinfo, assetFrontendAuthInfo] =
    useLoaderData() as [
      boolean,
      WebAppMainApi$UiContext,
      AdmUserApi$CurrentAuthedUser | null,
      AssetFrontendUserApi$CurrentAuthedUser | null,
      string,
    ];

  const [, setUic] = useAtom(uiContextState);
  const [,setAdminAreaAuthState] = useAtom(uiAdminAreaAuthState)
  const [,setAssetFrontendAuthState] = useAtom(uiAssetFrontendAuthState)
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (done) {
      return;
    }

    if (adminArea && adminAreaAuthinfo != null) {

      // If in controlpanel, needs authority ROLE_CP_ADMIN to see the area.
      //
      // ROLE_CP_ADMIN is granted as a plain authority, not an organisation-scoped one,
      // so it never appears in orgRoles (which only holds "ROLE:orgId:<id>" authorities,
      // see DomainGrantedAuthority.parseOrganisationAuthorities). The server reports it
      // as the CONTROLPANEL global permission instead, so that is what has to be read -
      // checking orgRoles here locked every user out of the control panel.
      let roleCpCheck = true
      if(uicontext.controlpanelContext != null){
        if(!adminAreaAuthinfo.globalPermissions?.includes(AdmUserApi$AuthInfoPermission.CONTROLPANEL)) {
          roleCpCheck = false
        }
      }

      // always: needs authority ROLE_ADMINAREA_USER to see the admin area
      if (
        roleCpCheck &&
        adminAreaAuthinfo.orgRoles?.includes(
          "ROLE_ADMINAREA_USER",
        )
      ) {
        setAdminAreaAuthState(adminAreaAuthinfo as AdmUserApi$CurrentAuthedUser)
      }

    } else if(!adminArea && assetFrontendAuthInfo != null) {
      // in asset mode, getting a result is OK to proceed
      setAssetFrontendAuthState(assetFrontendAuthInfo)
    }

    setUic(uicontext);
    setDone(true);

  }, [adminArea, uicontext, adminAreaAuthinfo, assetFrontendAuthInfo]);

  if (done) {
    return (
      <>
        <EnvironmentBanner label={uicontext.environmentBanner} />
        <Outlet />
      </>
    );
  }
  return null;
};
